import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { db } from "../../lib/firebase";
import { doc, collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import { Spinner } from "../../components/common/Spinner";
import { WalletCard } from "../../components/wallet/WalletCard";
import { getWalletTransactions } from "../../services/wallet.service";
import { AutoBadge, isAutoTx, txLabel, txIsCredit, txDate } from "../../components/wallet/WalletTxBits";

export default function UserDashboard() {
  const { user, loading: authLoading, setCurrentRole } = useAuth();
  const [wallet, setWallet] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "wallets", user.uid), (doc) => {
        if (doc.exists()) setWallet({ id: doc.id, ...doc.data() });
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    async function fetchData() {
      if (!user?.uid) return;
      setLoading(true);
      // Web orders use userId; some WhatsApp/legacy orders use customerId
      const byUser = await getDocs(query(collection(db, "orders"), where("userId", "==", user.uid)));
      const byCustomer = await getDocs(query(collection(db, "orders"), where("customerId", "==", user.uid)));
      const merged = new Map<string, any>();
      for (const d of [...byUser.docs, ...byCustomer.docs]) {
        merged.set(d.id, { id: d.id, ...d.data() });
      }
      const list = Array.from(merged.values());
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(list);
      const txs = await getWalletTransactions(user.uid);
      setTransactions(txs);
      setLoading(false);
    }
    fetchData();
  }, [user, wallet]);

  if (authLoading) return <Spinner />;
  if (!user) return <div className="p-10 text-center">Please log in</div>;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 font-sans text-gray-800">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hello, {user.name?.split(' ')[0]} 👋</h1>
          <p className="text-sm text-gray-500">{user.phone}</p>
        </div>
        {user.roles && user.roles.length > 1 && (
            <div className="flex bg-gray-100 p-1 rounded-lg">
                {user.roles.map(role => (
                <button key={role} onClick={() => setCurrentRole(role)} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${user.currentRole === role ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                    {role.toUpperCase()}
                </button>
                ))}
            </div>
        )}
      </div>

      <section className="mb-10">
        {wallet ? <WalletCard wallet={wallet} onUpdate={() => {}} /> : <div className="p-6 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center"><p className="text-gray-500 text-sm">Initializing Wallet...</p></div>}
      </section>

      <div className="grid md:grid-cols-2 gap-8">
          <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">⚡ Recent Transactions</h3>
              <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                 {transactions.length === 0 ? <p className="p-4 text-sm text-gray-400 text-center">No transactions yet</p> : (
                     <div className="divide-y divide-gray-100">
                         {transactions.slice(0, 5).map(tx => (
                             <div key={tx.id} className="p-3 flex justify-between items-center">
                                 <div>
                                     <div className="flex items-center gap-2">
                                        <p className="text-sm font-bold text-gray-800">{txLabel(tx)}</p>
                                        <AutoBadge tx={tx} />
                                        {tx.status === 'pending' && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold">Waiting Verification</span>}
                                     </div>
                                     {isAutoTx(tx) && tx.description && <p className="text-xs text-indigo-600">{tx.description}</p>}
                                     <p className="text-xs text-gray-400 capitalize">{isAutoTx(tx) ? 'Automatic' : (tx.paymentMethod || 'System')} • {txDate(tx)}</p>
                                 </div>
                                 <span className={`font-bold text-sm ${txIsCredit(tx) ? 'text-green-600' : 'text-gray-800'}`}>{txIsCredit(tx) ? '+' : '-'} ₹{tx.amount}</span>
                             </div>
                         ))}
                     </div>
                 )}
              </div>
          </div>

          <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg flex items-center gap-2">📦 Recent Orders</h3>
                <Link to="/orders" className="text-sm font-semibold text-orange-600 hover:text-orange-700">View all</Link>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                 {orders.length === 0 ? <p className="p-4 text-sm text-gray-400 text-center">No orders placed</p> : (
                     <div className="divide-y divide-gray-100">
                         {orders.slice(0, 5).map(order => (
                             <Link key={order.id} to={`/orders/${encodeURIComponent(order.parentOrderId || order.id)}`} className="p-3 flex justify-between items-center hover:bg-gray-50 block">
                                 <div>
                                     <p className="text-sm font-bold text-gray-800">Order #{order.id.slice(-5)}</p>
                                     <p className="text-xs text-gray-400">{order.items?.length || 1} Items • {order.status}</p>
                                 </div>
                                 <span className="font-bold text-sm text-gray-800">₹{order.totalAmount}</span>
                             </Link>
                         ))}
                     </div>
                 )}
              </div>
          </div>
      </div>
    </div>
  );
}